import type {
  DatesSetArg,
  EventClickArg,
  EventDropArg,
  MoreLinkArg
} from "@fullcalendar/core"
import dayGridPlugin from "@fullcalendar/daygrid"
import interactionPlugin, { type DateClickArg } from "@fullcalendar/interaction"
import listPlugin from "@fullcalendar/list"
import FullCalendar from "@fullcalendar/react"
import timeGridPlugin from "@fullcalendar/timegrid"
import { Button, Flex, Typography } from "antd"
import dayjs from "dayjs"
import React, { memo, useEffect, useMemo, useRef, useState } from "react"

import type {
  CalendarFilters as CalendarFiltersType,
  CalendarModalData
} from "../../redux/calendar.types.ts"
import { type Event } from "../../types/types.ts"
import { parseCalendarFocusDate } from "../../helpers/calendar-deep-link.ts"
import { eventMatchesSearch } from "../helpers/event-matches-search.ts"
import { resolveNewEventSlot } from "../helpers/new-event-slot.ts"
import { transformEventsForCalendar } from "../helpers/transform-events-for-calendar"
import { CalendarEventCell } from "./CalendarEventCell/CalendarEventCell"
import { CalendarFilters } from "./CalendarFilters/CalendarFilters"
import { CalendarHeader } from "./CalendarHeader"
import { CalendarNavigation } from "./CalendarNavigation"
import "./calendar.less"

const { Text } = Typography

interface CalendarProps {
  timezone: string
  focusDate?: string | null
  filters: CalendarFiltersType
  isMobile: boolean
  events: Event[]
  //
  createAllowed?: boolean
  editAllowed?: boolean
  //
  onSelectEvent: (id: string) => void
  onSetModalData: (data: CalendarModalData | null) => void
  onFetchEvents: ({ start, end }: Partial<DatesSetArg>) => void
  onFilterChange: (filter: Record<string, unknown>) => void
  onSetMoreInfo: (args: MoreLinkArg) => void
  onDropEvent: (arg: EventDropArg) => void
}

export const Calendar = memo(
  ({
    timezone,
    focusDate,
    filters,
    isMobile,
    createAllowed,
    editAllowed,
    events: apiEvents = [],
    onSelectEvent,
    onSetModalData,
    onFetchEvents,
    onFilterChange,
    onSetMoreInfo,
    onDropEvent
  }: CalendarProps) => {
    const isFirstRender = useRef(true)
    const calendarRef = useRef<FullCalendar | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const gridWrapRef = useRef<HTMLDivElement | null>(null)
    const getAPI = () => calendarRef?.current?.getApi()

    const [searchQuery, setSearchQuery] = useState("")

    const [nowLine, setNowLine] = useState({
      visible: false,
      top: 0,
      left: 0,
      width: 0,
      axisWidth: 0
    })

    // Client-side only, over whatever the server already returned for the
    // visible range — typing a search must never fire a request per
    // keystroke (the separate autocomplete box in CalendarFiltersSearch
    // still hits the server, since it searches beyond the loaded range).
    const searchedApiEvents = useMemo(
      () => apiEvents.filter((event) => eventMatchesSearch(event, searchQuery)),
      [apiEvents, searchQuery]
    )

    const events = transformEventsForCalendar(searchedApiEvents)
    const hasAllDayEvents = events.some((event) => event.allDay)
    const [currentView, setCurrentView] = useState(
      isMobile ? "listWeek" : "timeGridWeek"
    )

    const isFiltering =
      !!searchQuery.trim() ||
      filters.type !== null ||
      filters.entity_type !== null ||
      !!filters.participants?.length
    const showEmptyState = isFiltering && events.length === 0

    const handleClearFilters = () => {
      setSearchQuery("")
      onFilterChange({ type: null, entity_type: null, participants: null })
    }

    const now = dayjs().tz(timezone).format()

    const handleDateClick = (arg: DateClickArg) => {
      onSetModalData(resolveNewEventSlot(arg.dateStr, timezone, arg.allDay))
    }

    const handleEventClick = (clickInfo: EventClickArg) => {
      const event = clickInfo?.event
      const id = event?.id || event?.extendedProps?.id
      onSelectEvent(id)
    }

    const onPrevClick = () => {
      const calendarAPI = getAPI()
      if (calendarAPI) calendarAPI.prev()
    }

    const onNextClick = () => {
      const calendarAPI = getAPI()
      if (calendarAPI) calendarAPI.next()
    }

    const onTodayClick = () => {
      const calendarAPI = getAPI()

      if (calendarAPI) {
        calendarAPI.today()
        calendarAPI.gotoDate(calendarAPI.getDate())
      }
    }

    const onViewChange = (view: string) => {
      const calendarAPI = getAPI()
      setCurrentView(view)

      if (calendarAPI) calendarAPI.changeView(view)
    }

    const handleMoreLinkClick = (info: MoreLinkArg): any => {
      info.jsEvent.preventDefault()
      onSetMoreInfo(info)
      return () => null
    }

    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false
        return
      }

      if (timezone) {
        const calendarAPI = getAPI()
        const dateStr = dayjs().tz(timezone).format()

        if (calendarAPI) {
          calendarAPI.gotoDate(dateStr)
          calendarAPI.setOption("now", dateStr)
        }
      }
    }, [timezone])

    // A meeting opened from a Lead names its date in the URL, so the calendar
    // has to land on that page rather than on today.
    useEffect(() => {
      const target = parseCalendarFocusDate(focusDate)
      if (!target) return
      getAPI()?.gotoDate(target)
    }, [focusDate])

    useEffect(() => {
      const container = containerRef.current
      if (!container) return
      const resizeObserver = new ResizeObserver(() => getAPI()?.updateSize())
      resizeObserver.observe(container)
      return () => resizeObserver.disconnect()
    }, [])

    // FullCalendar's own now-indicator line only ever spans one day column —
    // that's a hard limit of how it lays the line out, not a config option.
    // Rather than reimplement its time-to-pixel math (timezone, slot height,
    // scroll position — all already correct there), this mirrors the
    // position of that line (kept in the DOM but invisible, see calendar.less)
    // onto a second, separately-painted line that spans the full grid width.
    useEffect(() => {
      const updateNowLine = () => {
        const wrap = gridWrapRef.current
        const line = wrap?.querySelector(
          ".fc-timegrid-now-indicator-line"
        ) as HTMLElement | null
        const gridEl = wrap?.querySelector(
          ".fc-timegrid-body"
        ) as HTMLElement | null
        const axisEl = wrap?.querySelector(
          ".fc-timegrid-axis-frame"
        ) as HTMLElement | null

        if (!wrap || !line || !gridEl) {
          setNowLine((prev) => (prev.visible ? { ...prev, visible: false } : prev))
          return
        }

        const wrapRect = wrap.getBoundingClientRect()
        const gridRect = gridEl.getBoundingClientRect()
        const axisWidth = axisEl?.getBoundingClientRect()?.width || 0
        const lineTop = line.getBoundingClientRect().top - wrapRect.top

        setNowLine({
          visible: true,
          top: lineTop,
          left: gridRect.left - wrapRect.left,
          width: gridRect.width,
          axisWidth
        })
      }

      updateNowLine()
      // FullCalendar's own DOM can still be mid-mount on the first tick.
      const mountRetry = setTimeout(updateNowLine, 200)

      const scroller = gridWrapRef.current?.querySelector(
        ".fc-timegrid-body"
      )?.closest(".fc-scroller")
      scroller?.addEventListener("scroll", updateNowLine)
      window.addEventListener("resize", updateNowLine)
      // FullCalendar's own indicator re-renders on its own ~minute timer;
      // this just has to catch up promptly after that, not drive the clock.
      const interval = setInterval(updateNowLine, 15000)

      return () => {
        scroller?.removeEventListener("scroll", updateNowLine)
        window.removeEventListener("resize", updateNowLine)
        clearInterval(interval)
        clearTimeout(mountRetry)
      }
    }, [currentView, events.length])

    return (
      <Flex
        vertical
        className={`size-full min-h-full ${hasAllDayEvents ? "" : "calendar-no-allday"}`}
        ref={containerRef}
      >
        <Flex vertical gap={12} className={"pb-3"}>
          <CalendarHeader
            createAllowed={createAllowed}
            onSetModalData={onSetModalData}
          />

          <CalendarFilters
            filters={filters}
            isMobile={isMobile}
            currentView={currentView}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            onViewChange={onViewChange}
            onFilterChange={onFilterChange}
            onSelectEvent={onSelectEvent}
            onClearFilters={handleClearFilters}
          />
        </Flex>
        <CalendarNavigation
          getAPI={getAPI}
          onTodayClick={onTodayClick}
          onPrevClick={onPrevClick}
          onNextClick={onNextClick}
        />

        {/* overflow-hidden + rounded-xl mirror .fc's own clipping (calendar.less)
            so the now-line — a sibling of <FullCalendar>, not a descendant —
            is actually clipped to the calendar's rounded border instead of
            painting past it; without this the line's own box had no boundary
            of its own to stop at. */}
        <div
          className={"relative flex-1 min-h-0 overflow-hidden rounded-xl"}
          ref={gridWrapRef}
        >
          {nowLine.visible && (
            <>
              <div
                className={"calendar-now-axis-label"}
                style={{
                  top: nowLine.top,
                  left: nowLine.left,
                  width: nowLine.axisWidth
                }}
              >
                {dayjs().tz(timezone).format("h:mm A")}
              </div>
              <div
                className={"calendar-now-line"}
                style={{
                  top: nowLine.top,
                  left: nowLine.left + nowLine.axisWidth,
                  width: nowLine.width - nowLine.axisWidth
                }}
              />
            </>
          )}

          {showEmptyState && (
            <Flex
              vertical
              align={"center"}
              justify={"center"}
              gap={4}
              className={
                "absolute inset-0 z-10 pointer-events-none pt-24 bg-white/70"
              }
            >
              <Text strong className={"text-base"}>
                No appointments found
              </Text>
              <Text type={"secondary"} className={"text-sm"}>
                Try changing your filters or search.
              </Text>
              <Button
                size={"small"}
                className={"pointer-events-auto mt-1"}
                onClick={handleClearFilters}
              >
                Clear filters
              </Button>
            </Flex>
          )}

          <FullCalendar
          views={{
            timeGridDay: {
              titleFormat: { year: "numeric", month: "long", day: "numeric" } // "January 8, 2026"
            },
            listDay: {
              titleFormat: { year: "numeric", month: "long", day: "numeric" } // "January 8, 2026"
            },
            dayGridMonth: {
              titleFormat: { year: "numeric", month: "long" } // "January 2026"
            },
            timeGridWeek: {
              titleFormat: { year: "numeric", month: "short", day: "numeric" } // "Jan 6, 2026"
            },
            listWeek: {
              titleFormat: { year: "numeric", month: "short", day: "numeric" } // "Jan 6, 2026"
            },
            listMonth: {
              titleFormat: { year: "numeric", month: "short", day: "numeric" } // "Jan 6, 2026"
            }
          }}
          // handleWindowResize={false}
          datesSet={onFetchEvents}
          ref={calendarRef}
          plugins={[
            dayGridPlugin,
            timeGridPlugin,
            interactionPlugin,
            listPlugin
          ]}
          titleFormat={{
            weekday: "short",
            month: "short",
            day: "2-digit"
          }}
          dayHeaderContent={(args) => {
            // Use date directly without time zone conversion
            const date = dayjs.utc(args.date)
            // OR if you need a calendar timezone:
            // const date = dayjs(args.date).tz(args.view.dateEnv.timeZone, true)

            const type = args?.view?.type
            const isToday = args?.isToday

            if (["dayGridMonth"]?.includes(type)) {
              return <Text strong>{date.format("ddd")}</Text>
            }

            // Week/Day headers carry an actual date, so jumping to that
            // day's Day view reads as natural; Month's header row is just
            // weekday names shared across the whole grid, so it stays inert.
            const goToDay = () => {
              const calendarAPI = getAPI()
              if (!calendarAPI) return
              calendarAPI.changeView("timeGridDay", args.date)
              setCurrentView("timeGridDay")
            }

            return (
              <Flex
                vertical
                className={"px-1 cursor-pointer"}
                gap={2}
                onClick={goToDay}
              >
                <Text
                  className={`uppercase tracking-wide text-[11px] ${isToday ? "text-inherit font-bold" : ""}`}
                  strong
                >
                  {date.format("ddd")}
                </Text>
                <Text
                  className={`text-base leading-none ${isToday ? "text-inherit font-bold" : "font-semibold"}`}
                >
                  {date.format("MMM D")}
                </Text>
              </Flex>
            )
          }}
          initialView={currentView}
          now={() => dayjs().tz(timezone).format()}
          initialDate={now}
          // locale={enGbLocale}
          // locales={[enGbLocale]}
          firstDay={0}
          droppable={false}
          editable={!!editAllowed}
          eventDurationEditable={false}
          //
          selectable={false}
          allDayText={"ALL DAY"}
          nowIndicator
          // FC's own line/arrow render nothing visible (see calendar.less) —
          // the full-width overlay rendered alongside this component's
          // return is what's actually painted, mirroring this line's
          // position. FC still needs `nowIndicator` on to lay that line out
          // at all, which is the only reason it's kept in the DOM.
          nowIndicatorContent={() => <></>}
          eventOverlap
          slotEventOverlap
          selectMirror
          dayMaxEvents={2} // Для dayGridMonth
          dayMaxEventRows={3} // Альтернатива для dayGrid
          eventMaxStack={3} // Максимум стеків events в одному слоті
          eventMinHeight={28} // fits the time + type-dot lines even on a 15-min slot
          weekends
          dragScroll={false}
          events={events}
          height="100%"
          timeZone={timezone}
          headerToolbar={false}
          slotMinTime="00:00:00"
          slotMaxTime="23:59:00"
          scrollTime={{ hours: 7 }}
          slotLabelFormat={{
            hour: "numeric",
            minute: "2-digit",
            hour12: true
          }}
          dayHeaderFormat={{
            weekday: "short",
            day: "numeric"
          }}
          eventTimeFormat={{
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            meridiem: "short"
          }}
          eventDidMount={(info) => {
            const event = info.event
            const el = info.el

            // Примусово застосувати кольори з події
            if (event.backgroundColor) {
              el.style.backgroundColor = event.backgroundColor
            }
            if (event.borderColor) {
              el.style.borderColor = event.borderColor
            }
            if (event.textColor) {
              el.style.color = event.textColor
            }

            // Для тексту всередині події
            const titleEl = el.querySelector(".fc-event-title") as HTMLElement

            if (titleEl && event.textColor) {
              titleEl.style.color = event.textColor
            }

            const timeEl = el.querySelector(".fc-event-time") as HTMLElement
            if (timeEl && event.textColor) {
              timeEl.style.color = event.textColor
            }
          }}
          dateClick={createAllowed ? handleDateClick : undefined}
          moreLinkClick={handleMoreLinkClick}
          eventClick={handleEventClick}
          eventContent={CalendarEventCell}
          eventDrop={onDropEvent}
          // eventResize={(info) => console.log("Event resized:", info)}
          // select={(props) => console.log("select:", props)}
        />
        </div>
      </Flex>
    )
  }
)

Calendar.displayName = "Calendar"
